import React from 'react';
import { Users, MapPin, Building, TrendingUp, Loader2 } from 'lucide-react';
import { useTimeline } from '../hooks/usePortfolioData';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Timeline = () => {
  const { timeline, loading } = useTimeline();

  const careerMilestones = [
    {
      year: '2016',
      title: 'Career Launch',
      description: 'Started as Site Architect, establishing foundation in construction and project delivery'
    },
    {
      year: '2017',
      title: 'Professional Growth',
      description: 'Advanced through multiple roles at FineTouch, gaining comprehensive project management experience'
    },
    {
      year: '2022',
      title: 'Major Project Success',
      description: 'Successfully delivered IKEA Mall of Arabia (21,000 m²) - largest project to date'
    },
    {
      year: '2025',
      title: 'Leadership Role',
      description: 'Promoted to Technical Team Leader at Sigma, mentoring engineers and leading technical coordination'
    },
    {
      year: '2026',
      title: 'Regional Expansion',
      description: 'Appointed Head of Technical Office for Egypt & Saudi Arabia operations'
    },
    {
      year: '2026',
      title: 'New Chapter',
      description: 'Joined Klad as Head of Technical Office, building the technical function from the ground up'
    }
  ];

  const achievements = [
    { metric: '20+', label: 'Engineers Led', icon: Users },
    { metric: '50+', label: 'Projects Delivered', icon: Building },
    { metric: '2', label: 'Countries', icon: MapPin },
    { metric: '100%', label: 'Client Satisfaction', icon: TrendingUp }
  ];

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-gray-400" size={48} />
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Header */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Professional Timeline</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            A decade-long journey of professional growth, from site architect to regional
            technical office leader, marked by consistent advancement and increasing responsibilities.
          </p>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            {achievements.map((achievement, index) => {
              const IconComponent = achievement.icon;
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="text-gray-600" size={32} />
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-2">{achievement.metric}</div>
                    <div className="text-sm text-gray-600">{achievement.label}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Career Timeline */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Career Journey</h2>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-300"></div>

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div key={index} className="relative flex items-start">
                  {/* Timeline dot */}
                  <div className="absolute left-6 w-4 h-4 bg-gray-900 rounded-full border-4 border-white shadow-md"></div>

                  {/* Content */}
                  <div className="ml-16 flex-1">
                    <Card className={`hover:shadow-lg transition-shadow ${
                      item.companyGroup === 'klad'
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-white'
                    }`}>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <Badge variant="outline" className="mb-2">{item.year}</Badge>
                            <h3 className="text-xl font-semibold text-gray-900 mb-1">{item.position}</h3>
                            <p className="text-gray-600 font-medium">
                              {item.company}
                              {item.companyGroup === 'sigma' && item.position === 'Head of Technical Office' && (
                                <span className="ml-2 text-xs text-blue-600 font-semibold">(Promoted)</span>
                              )}
                            </p>
                          </div>
                        </div>
                        <p className="text-gray-700">{item.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Career Milestones */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Key Milestones</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {careerMilestones.map((milestone, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-gray-900 text-white rounded-lg flex items-center justify-center font-bold text-sm">
                      {milestone.year}
                    </div>
                    <h3 className="font-semibold text-lg text-gray-900">{milestone.title}</h3>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{milestone.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Future Vision */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">Looking Forward</h2>
          <p className="text-lg leading-relaxed text-gray-300 mb-8">
            Now building the technical office at Klad, after leading it across Egypt and Saudi Arabia,
            my focus remains on delivering exceptional architectural projects that push the boundaries
            of design and technical excellence. I'm committed to fostering innovation, mentoring the
            next generation of architects, and contributing to the built environment of both nations.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-3">Innovation</h3>
              <p className="text-gray-300 text-sm">Embracing new technologies and methodologies in project delivery</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-3">Mentorship</h3>
              <p className="text-gray-300 text-sm">Developing the next generation of architectural professionals</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-3">Excellence</h3>
              <p className="text-gray-300 text-sm">Maintaining the highest standards in every project undertaken</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Timeline;
